var _excluded = ["isDisabled", "isLoading", "isActive", "isFullWidth", "children", "leftIcon", "rightIcon", "loadingText", "type", "spinner", "className", "as"],
  _excluded2 = ["label", "spacing", "children", "className", "__css"];
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
import * as React from 'react';
import { forwardRef, omitThemingProps, tqc, useMultiStyleConfig } from '../../system';
import { __DEV__, cx, dataAttr, mergeWith } from '../../utils';
import { Spinner } from '../spinner';
import { Fade } from '../transition';
import { useButtonGroup } from './ButtonGroup';
import { ButtonIcon } from './ButtonIcon';
export var Button = forwardRef((props, ref) => {
  var _styles$button$_focus, _styles$button;
  var group = useButtonGroup();
  var styles = useMultiStyleConfig('Button', _objectSpread(_objectSpread({}, group), props));
  var _omitThemingProps = omitThemingProps(props),
    {
      isDisabled = group === null || group === void 0 ? void 0 : group.isDisabled,
      isLoading,
      isActive,
      isFullWidth,
      children,
      leftIcon,
      rightIcon,
      loadingText,
      type = 'button',
      spinner,
      className,
      as
    } = _omitThemingProps,
    rest = _objectWithoutProperties(_omitThemingProps, _excluded);

  /**
   * When button is used within ButtonGroup (i.e flushed with sibling buttons),
   * it is important to add a `zIndex` on focus.
   *
   * So let's read the component styles and then add `zIndex` to it.
   */
  var _focus = mergeWith({}, (_styles$button$_focus = styles === null || styles === void 0 ? void 0 : (_styles$button = styles.button) === null || _styles$button === void 0 ? void 0 : _styles$button['_focus']) !== null && _styles$button$_focus !== void 0 ? _styles$button$_focus : {}, {
    zIndex: 1
  });
  var buttonStyles = _objectSpread(_objectSpread({
    display: 'inline-flex',
    appearance: 'none',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 250ms',
    userSelect: 'none',
    position: 'relative',
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
    outline: 'none',
    width: isFullWidth ? '100%' : 'auto'
  }, (styles === null || styles === void 0 ? void 0 : styles.button) && styles.button), !!group && {
    _focus
  });
  return /*#__PURE__*/React.createElement(tqc.button, _extends({
    disabled: isDisabled || isLoading,
    ref: ref,
    as: as,
    type: as ? undefined : type,
    "data-active": dataAttr(isActive),
    "data-loading": dataAttr(isLoading),
    __css: buttonStyles,
    className: cx('tqc-button', className)
  }, rest), leftIcon && !isLoading && /*#__PURE__*/React.createElement(ButtonIcon, {
    __css: styles.leftIcon
  }, leftIcon), isLoading ? loadingText || children : children, /*#__PURE__*/React.createElement(Fade, {
    in: isLoading
  }, /*#__PURE__*/React.createElement(ButtonSpinner, {
    __css: styles.spinner,
    label: loadingText
  }, spinner)), rightIcon && !isLoading && /*#__PURE__*/React.createElement(ButtonIcon, {
    __css: styles.rightIcon
  }, rightIcon));
});
if (__DEV__) {
  Button.displayName = 'Button';
}
if (__DEV__) {
  ButtonIcon.displayName = 'ButtonIcon';
}
var ButtonSpinner = props => {
  var {
      label,
      spacing,
      children = /*#__PURE__*/React.createElement(Spinner, {
        color: "currentColor",
        width: "1em",
        height: "1em"
      }),
      className,
      __css
    } = props,
    rest = _objectWithoutProperties(props, _excluded2);
  var _className = cx('tqc-button__spinner', className);
  var spinnerStyles = _objectSpread({
    display: 'flex',
    alignItems: 'center',
    position: label ? 'relative' : 'absolute',
    marginRight: label ? spacing : 0
  }, __css);
  return /*#__PURE__*/React.createElement(tqc.div, _extends({
    className: _className
  }, rest, {
    __css: spinnerStyles
  }), children);
};
if (__DEV__) {
  ButtonSpinner.displayName = 'ButtonSpinner';
}